<script setup lang="ts">
import { vMaska } from 'maska/vue'
import type { MaskInputOptions } from 'maska'
import { type UIInputBaseProps, type UseFieldEmits, LiveValidateType } from '../../types'
import { ref, computed, useAttrs, watch, useFocus, useField, useId, onMounted } from '#imports'

export interface UIInputProps extends UIInputBaseProps {
    modelValue?: string | number
    type?: HTMLInputElement['type']
    mask?: MaskInputOptions
    hint?: string
}

const props = withDefaults(defineProps<UIInputProps>(), {
    modelValue: undefined,
    type: 'input',
    mask: undefined,
    suffix: undefined,
    testId: undefined,
    dataTest: undefined,
    errorMessages: undefined,
    rules: undefined,
    autofocus: false,
    autocomplete: undefined,
    hint: undefined,
    liveValidate: undefined,
    staticLabel: false,
})
const emit = defineEmits<UseFieldEmits>()

const attrs = useAttrs()
const slots = defineSlots<{
    prepend?(props: {
        imageAttrs: {
            class: any[]
        }
        iconAttrs: {
            class: any[]
        }
        context: typeof context
    }): any
    default(props: typeof context): any
    hint?(props: Record<string, any>): any
    displayValue?(props: Record<string, any>): any
    append?(props: {
        imageAttrs: {
            class: any[]
        }
        iconAttrs: {
            class: any[]
        }
        context: typeof context
    }): any
    error?(): any
}>()

const prepend = ref<HTMLInputElement | null>(null)
const append = ref<HTMLInputElement | null>(null)
const inputRef = ref<HTMLInputElement | null>(null)
const labelRef = ref<HTMLElement | null>(null)
const prependWidth = ref<number>(0)
const appendWidth = ref<number>(0)
const unmaskedValue = defineModel<string | number>('unmaskedValue')

const maskaOptions = computed<MaskInputOptions>(() => {
    const currentMask = typeof props.mask?.mask === 'function'
        ? props.mask.mask(props.modelValue as string)
        : props.mask?.mask

    return {
        mask: currentMask,
        onMaska: (detail: { masked: string, unmasked: string, completed: boolean }) => {
            const shouldValidate = props.liveValidate === LiveValidateType.INPUT || props.liveValidate === LiveValidateType.CHANGE || !!context.errorMessage.value

            context.handleChange(detail.masked, shouldValidate)
            unmaskedValue.value = detail.unmasked || detail.masked
        },
        number: props.mask?.number,
        tokens: props.mask?.tokens,
        eager: true,
    }
})

const component = computed(() => {
    if (props.type === 'textarea') {
        return 'textarea'
    }

    return 'input'
})

const inputAndLabelId = useId()
const { focused } = useFocus(inputRef)

const { context, on } = useField({
    props: () => props,
    emit,
    on: {
        enableChange: !props.mask,
        enableInput: !props.mask,
        forceValidateOnInput: props.liveValidate === LiveValidateType.INPUT,
        forceValidateOnBlur: props.liveValidate === LiveValidateType.BLUR,
        forceValidateOnChange: props.liveValidate === LiveValidateType.CHANGE,
    },
    options: {
        initialValue: props.modelValue
    }
})

// Slot contexts
const prependSlotContext = computed(() => {
    return {
        iconAttrs: {
            size: 24,
            class: [
                'input-field__icon',
                'input-field__icon--prepended'
            ]
        },
        imageAttrs: {
            width: 34,
            class: [
                'input-field__image',
                'input-field__image--prepended'
            ]
        },
        context,
        focus: focused.value
    }
})
const appendSlotContext = computed(() => {
    return {
        iconAttrs: {
            size: 24,
            class: [
                'input-field__icon',
                'input-field__icon--appended'
            ]
        },
        imageAttrs: {
            width: 34,
            class: [
                'input-field__image',
                'input-field__image--appended'
            ]
        },
        context,
        focus: focused.value
    }
})

function handlePrependWidth() {
    if (!slots?.prepend || !prepend.value) return

    prependWidth.value = prepend.value?.offsetWidth
}

function handleAppendWidth() {
    if (!slots?.append || !append.value) return

    appendWidth.value = append.value?.offsetWidth
}

watch(focused, (focus) => {
    emit('focus', focus)
}, {
    immediate: true
})

defineExpose({
    context,
    inputRef
})

onMounted(() => {
    handlePrependWidth()
    handleAppendWidth()

    if (props.autofocus) {
        setTimeout(() => {
            inputRef.value?.focus()
        }, 10)
    }
})
</script>

<template>
    <div
        :class="[
            'input-field',
            {
                'input-field--focused': (!!context.value.value || focused),
                'input-field--error': context.meta.validated && !context.meta.valid,
                'input-field--has-display-value': !!slots?.displayValue,
                'input-field--static': props.staticLabel,
            }
        ]"
    >
        <div class="input-field__input-container">
            <div v-if="slots.prepend" ref="prepend" class="input-field__prepend">
                <slot name="prepend" v-bind="prependSlotContext" />
            </div>
            <!-- Input -->
            <component
                :is="component"
                :id="inputAndLabelId"
                ref="inputRef"
                v-maska="props.mask?.mask ? maskaOptions : undefined"
                :disabled="disabled"
                :name="name"
                :value="context.value.value"
                :type="props.type"
                v-bind="attrs"
                :placeholder="placeholder"
                :data-test="props.dataTest"
                class="input-field__input"
                :style="{
                    paddingRight: appendWidth ? `${appendWidth + 15}px` : undefined,
                    paddingLeft: prependWidth ? `${prependWidth + 15}px` : undefined,
                }"
                :autocomplete="props.autocomplete"
                v-on="on"
            />

            <!-- Label -->
            <label
                ref="labelRef"
                class="input-field__label"
                :for="inputAndLabelId"
                :style="{
                    paddingRight: appendWidth ? `${appendWidth}px` : undefined,
                    paddingLeft: prependWidth ? `${prependWidth}px` : undefined,
                }"
            >
                <slot v-bind="{ ...context, focus: focused }" />
            </label>

            <div v-if="slots.displayValue && (focused || !!context.value.value)" class="input-field__display-value">
                <slot name="displayValue" v-bind="{ ...context, focus: focused }" />
            </div>

            <div v-if="slots.append" ref="append" class="input-field__append">
                <slot name="append" v-bind="appendSlotContext" />
            </div>
        </div>

        <!-- Hint -->
        <p v-if="(!!props.hint || !!slots.hint)" class="input-field__hint">
            <slot name="hint">
                {{ props.hint }}
            </slot>
        </p>

        <!-- Errors -->
        <div class="input-field__error">
            <slot name="error">
                <UIErrorMessages :messages="context.errors.value" :data-test="props.dataTest" />
            </slot>
        </div>
    </div>
</template>
